import * as XLSX from 'xlsx';
import { allows, rightsOf, unitsOf } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { normalizeLinks } from '../../lib/links';
import { storedDocuments } from '../../lib/documents';
import { statusLabel } from '../patients/patientModel';

// Archives (Firestore collection "archives", protected space): the files of deceased or
// discharged (exeat) patients, with a comment and their documents. An entry keeps a copy of the
// patient's identity (`patient`) taken when it was archived, and its unit (`service`).
export const ARCHIVABLE = ['Opéré-décédé', 'Opéré-Exeat'];
export const isArchivable = (patient) => Boolean(patient) && ARCHIVABLE.includes(patient.statut);

// Kept by the archivists by level; the other accounts given the space consult (grid rule).
export const archiveAccess = (user) => {
  const rights = rightsOf(user);
  const isAdmin = user.role === 'Admin';
  const myUnits = unitsOf(user.service);
  const owns = (entry) => isAdmin || unitsOf(entry && entry.service).some((unit) => myUnits.includes(unit));
  return {
    isAdmin,
    myUnits,
    visible: owns,
    ownsPatient: (patient) => isAdmin || unitsOf(patient && patient.services).some((unit) => myUnits.includes(unit)),
    canAdd: allows(rights, 'archives', 'modifier'),
    canEdit: (entry) => allows(rights, 'archives', 'modifier') && owns(entry),
    canDelete: (entry) => allows(rights, 'archives', 'supprimer') && owns(entry),
    canExport: allows(rights, 'archives', 'tout'),
  };
};

export const patientIdOf = (entry) => entry.patientId || (entry.patient && entry.patient._id) || '';

// The patient as known today, else the copy taken when archiving.
export const patientOf = (entry, patientsById) => patientsById[patientIdOf(entry)] || entry.patient || {};

// Identity kept with the entry (enough to read it even if the file changes later).
export const snapshotOf = (patient) => ({
  _id: patient._id,
  nom: patient.nom || '',
  dossierNumber: patient.dossierNumber || '',
  statut: patient.statut || '',
  services: patient.services || '',
  dateNaissance: patient.dateNaissance || '',
  dateEntree: patient.dateEntree || '',
  dateSortie: patient.dateSortie || '',
});

export const documentsOf = (entry) => storedDocuments(entry.documents);
export const legacyLinks = (entry) => normalizeLinks(entry.dropboxLinks);
export const countFiles = (entry) => documentsOf(entry).length + legacyLinks(entry).length;

export const searchText = (entry, patient) =>
  [
    patient.nom,
    patient.dossierNumber,
    statusLabel(patient.statut),
    entry.comment,
    entry.service,
    formatDate(entry.timestamp),
    ...documentsOf(entry).map((item) => item.name),
    ...legacyLinks(entry).map((item) => item.name),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// Same columns as the former export, plus the status and the unit.
export const exportArchives = (entries) => {
  const rows = entries.map(({ entry, patient }) => ({
    'Numéro de dossier': patient.dossierNumber || '',
    Patient: patient.nom || '',
    Statut: statusLabel(patient.statut) || '',
    "Date d'archivage": formatDate(entry.timestamp),
    Commentaire: String(entry.comment || '').trim(),
    Documents: countFiles(entry),
    Unité: entry.service || '',
  }));
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows), 'Archives');
  XLSX.writeFile(book, 'archives.xlsx');
};
